import {EventBus} from '@/plugins/eventBus';
import {Form} from '@/plugins/FWForms/index';
import axios from 'axios';
import * as types from '@/store/mutation-types';
import {StorageService} from '@/services/storage.service';
import {createUrlQueryString} from '@/utils/urls';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import {push} from 'notivue';
import type {PaymentInterface} from '@/types/payment';
import {handleApiError} from '@/utils/errorHandler.ts';


export interface RefundData {
    uuid: string;
    amount: number;
    description: string;
}

export interface CapturePaymentInterface {
    uuid: string;
    amount: number;
    description?: string;
    security_deposit?: number;
}

type PaymentMethod = {
    billing_details: {
        address: {
            city: string | null,
            country: string | null,
            line1: string | null,
            line2: string | null,
            postal_code: string | null,
            state: string | null,
        },
        email: string | null,
        name: string | null,
        phone: string | null,
    },
    brand: string,
    card: {
        brand: string,
        checks: {
            address_line1_check: string,
            address_postal_code_check: string,
            cvc_check: string,
        },
        country: string,
        exp_month: number,
        exp_year: number,
        fingerprint: string,
        funding: string,
        generated_from: string,
        last4: string,
        networks: {
            available: Array<string>,
            preferred: string,
        },
        three_d_secure_usage: {
            supported: boolean,
        },
        wallet: string,
    },
    exp_month: number,
    exp_year: number,
    funding: string,
    id: string,
    is_default: boolean,
    last4: string,
    name: string,
}

export interface StripePaymentIntent {
    amount: number,
    amount_details: any,
    automatic_payment_methods: any,
    canceled_at: any,
    cancellation_reason: any,
    capture_method: string,
    client_secret: string,
    confirmation_method: string,
    created: number,
    currency: string,
    description: string,
    id: string,
    last_payment_error: any,
    livemode: boolean,
    next_action: any,
    object: string,
    payment_method: string,
    payment_method_configuration_details: any,
    payment_method_types: Array<string>,
    processing: any,
    receipt_email: any,
    setup_future_usage: string,
    shipping: any,
    source: any,
    status: string,
}

export interface PaymentsState {
    loading: Record<string, boolean>,
    payments: PaymentInterface[],
    payment: any,
    newPayment: {
        method: string,
        amount: number,
        date: string,
        security_deposit: number,
        keep_deposit_amount: number,
        keep_deposit_reason: string,
        description: string,
        transaction_type: string,
        payment_type: string,
        newCard: boolean,
        nameOnCard: string,
        paymentMethod: any,
        credit_card_id: any,
        capture_method: string,
    },
    stripeKey: string,
    payment_methods: PaymentMethod[],
    StripeIntent: any,
    paymentForm: Form<any>,
    customPaymentMethods: string[],
    refundDialog: {
        isOpen: boolean,
        payment: PaymentInterface | null,
    },
    viewPaymentItemDialog: boolean,
}

// state
const state: PaymentsState = {
    loading: {
        cardBtnLoading: false,
        addPaymentMethod: false,
        cancelPaymentIntent: false,
        refund: false,
        refundPayment: false,
        cancelPayment: false,
        deletePayment: false,
        createPayment: false,
        capturePayment: false,
        deleteCustomPayment: false,
        sendPaymentViaSms: false,
        fetchPayment: false,
        fetchPaymentMethods: false,
        updatePaymentMethods: false,
    },
    payments: [],
    payment: {},
    newPayment: {
        method: '',
        amount: 0,
        date: new Date().toISOString().substring(0, 10),
        security_deposit: 0,
        keep_deposit_amount: 0,
        keep_deposit_reason: '',
        description: '',
        transaction_type: 'charge',
        payment_type: 'cash',
        newCard: false,
        nameOnCard: '',
        paymentMethod: null,
        credit_card_id: null,
        capture_method: 'automatic',
    },
    stripeKey: import.meta.env.VUE_APP_STRIPE_KEY,
    payment_methods: [] as PaymentMethod[],
    StripeIntent: {},
    paymentForm: new Form(),
    customPaymentMethods: [],
    refundDialog: {
        isOpen: false,
        payment: null,
    },
    viewPaymentItemDialog: false,
};


const PaymentsModule: Module<PaymentsState, RootState> = {
    namespaced: true,
    state,
    mutations: {
        [types.SET_PAYMENTS_LOADING](state: PaymentsState, loading: { subject: string, loading: boolean }) {
            state.loading[loading.subject] = loading.loading;
        },
        [types.SET_PAYMENT](state: PaymentsState, payment: PaymentInterface) {
            state.payment = payment;
        },
        [types.SET_PAYMENTS](state, payments: PaymentInterface[]) {
            state.payments = payments;
        },
        [types.SET_PAYMENT_METHODS](state, payment_methods: PaymentMethod[]) {
            state.payment_methods = payment_methods;
        },
        [types.SET_CUSTOM_PAYMENT_METHODS](state, methods) {
            state.customPaymentMethods = methods;
        },
        [types.SET_STRIPE_INTENT](state, data) {
            state.StripeIntent = data;
        },
        [types.RESET_NEW_PAYMENT](state) {
            state.newPayment = {
                method: '',
                amount: 0,
                date: new Date().toISOString().substring(0, 10),
                security_deposit: 0,
                keep_deposit_amount: 0,
                keep_deposit_reason: '',
                description: '',
                transaction_type: 'charge',
                payment_type: 'cash',
                newCard: false,
                nameOnCard: '',
                paymentMethod: null,
                credit_card_id: null,
                capture_method: 'automatic',
            }
        },
        [types.SET_PAYMENT_DETAILS](state, {method, transaction_type, payment_type, newCard}) {
            if (transaction_type) state.newPayment.transaction_type = transaction_type;
            if (payment_type) state.newPayment.payment_type = payment_type;
            if (newCard !== undefined) state.newPayment.newCard = newCard;
            if (method) state.newPayment.method = method;
        },
        [types.SET_PAYMENT_AMOUNT](state, amount: number) {
            state.newPayment.amount = amount;
        },
        [types.SET_PAYMENT_CAPTURE_METHOD](state, capture_method: string) {
            state.newPayment.capture_method = capture_method;
        },
        [types.SET_PAYMENT_SECURITY_DEPOSIT](state, security_deposit: number) {
            state.newPayment.security_deposit = security_deposit;
        },
        [types.SET_PAYMENT_DESCRIPTION](state, description: string) {
            state.newPayment.description = description;
        },
        [types.OPEN_REFUND_DIALOG](state, payment: PaymentInterface) {
            state.refundDialog.payment = payment;
            state.refundDialog.isOpen = true;
        },
        [types.CLOSE_REFUND_DIALOG](state) {
            state.refundDialog.isOpen = false;
            state.refundDialog.payment = null;
        },
        [types.SET_VIEW_PAYMENT_ITEM_DIALOG](state: PaymentsState, isOpen: boolean) {
            state.viewPaymentItemDialog = isOpen;
        },
        SET_PAYMENT_FORM_BUSY(state: PaymentsState, busy: boolean) {
            state.paymentForm.busy = busy;
        },
        SET_PAYMENT_FORM_SUCCESSFUL(state: PaymentsState, successful: boolean) {
            state.paymentForm.successful = successful;
        },
        SET_PAYMENT_FORM_ERRORS(state: PaymentsState, errors: Record<string, string[]>) {
            state.paymentForm.errors.set(errors);
        },
        CLEAR_PAYMENT_FORM_ERRORS(state: PaymentsState) {
            state.paymentForm.errors.clear();
        },
        CLEAR_PAYMENT_FORM_ERROR(state: PaymentsState, field: string) {
            state.paymentForm.errors.clear(field);
        }
    },
    actions: {
        async fetchPayment({commit}, payment_id: string) {
            try {
                const responseData = await performApiRequest(commit, 'SET_PAYMENTS_LOADING', 'fetchPayment', () => axios.get(`/v1/payments/${payment_id}`));
                commit('SET_PAYMENT', responseData.payment);
                return responseData.payment;
            } catch (error) {
                console.error(error)
            }
        },

        fetchPayments({commit}, attributes) {
            const query = createUrlQueryString(attributes, true);

            axios.get(`/v1/payments${query}`)
                .then((response) => {
                    commit('SET_PAYMENTS', response.data.payments);
                    EventBus.$emit('fetch-payments-success');
                }).catch(() => {
            }).finally(() => {
            });
        },

        async fetchPaymentMethods({commit}) {
            try {
                const responseData = await performApiRequest(commit, 'SET_PAYMENTS_LOADING', 'fetchPaymentMethods', () => axios.get('/v1/stripe/payment-methods'));
                commit('SET_PAYMENT_METHODS', responseData.cards);
            } catch (error) {
                console.error(error)
            }
        },
        async updateDefaultBillingDetails({commit}, data) {
            try {
                await performApiRequest(commit,
                    'SET_PAYMENTS_LOADING',
                    'updatePaymentMethods',
                    () => axios.patch(`/v1/stripe/payment-methods/${data.payment_method_id}/billing-details`, data)
                );
                push.success({
                    message: 'Payment method updated successfully',
                });
            } catch (error) {
                handleApiError(error);
            }
        },

        createSetupIntent({commit}) {
            axios.post('/v1/stripe/create-setup-intent')
                .then((response) => {
                    commit('SET_STRIPE_INTENT', response.data.intent);
                }).catch(() => {
            }).finally(() => {
            });
        },

        async addPaymentMethod({commit}, paymentIntent: StripePaymentIntent) {
            try {
                const responseData = await performApiRequest(commit, 'SET_PAYMENTS_LOADING', 'addPaymentMethod', () => axios.post('/v1/stripe/payment-method', paymentIntent));
                commit(types.SET_PAYMENT_METHODS, responseData?.cards || []);
                commit('company/SET_COMPANY', responseData?.company, {root: true});
                EventBus.$emit('add-payment-method-success');
            } catch (error) {
                console.error(error)
            }
        },

        /**
         * Capture a payment authorization
         */
        capturePayment({commit}, capture: CapturePaymentInterface) {
            commit('SET_PAYMENTS_LOADING', {subject: 'capturePayment', loading: true});

            axios.post(`/v1/payments/${capture.uuid}/capture`, capture)
                .then((response) => {
                    commit('SET_PAYMENT', response.data.payment);
                    EventBus.$emit('capture-payment-success');

                    push.success({
                        message: 'Payment was captured successfully',
                    });
                }).catch((e) => {
                push.error({
                    message: e?.response?.data?.message || 'There was an error',
                });
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'capturePayment', loading: false});
            });
        },

        /**
         * Create a payment
         */
        createPayment({state, commit}, payment) {
            commit('SET_PAYMENTS_LOADING', {subject: 'createPayment', loading: true});


            axios.post('/v1/payments', {
                payment,
            }).then((response) => {
                EventBus.$emit('create-payment-success', response.data.payment);
                commit(types.SET_PAYMENT, response.data.payment);

                push.success({
                    message: 'Success',
                });
            }).catch((e) => {
                push.error({
                    message: e?.response?.data?.message || 'There was an error',
                });
                state.paymentForm.errors.set(e?.response?.data?.message || 'There was an error');
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'createPayment', loading: false});
            });
        },

        /**
         * Create a manual payment
         */
        createManualPayment({commit}, payment) {
            commit('SET_PAYMENTS_LOADING', {subject: 'createPayment', loading: true});

            axios.post('/v1/payments', {payment})
                .then((response) => {
                    EventBus.$emit('create-payment-success', response.data.payment);
                    commit(types.SET_PAYMENT, response.data.payment);

                    push.success({
                        message: 'Success',
                    });
                }).catch((e) => {
                console.dir(e);
                push.error({
                    message: e?.response?.data?.message || 'There was an error',
                });
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'createPayment', loading: false});
            });
        },

        async cancelPayment({commit}, uuid: string) {
            try {
                const responseData = await performApiRequest(commit, 'SET_PAYMENTS_LOADING', 'cancelPayment', () => axios.post(`/v1/payments/${uuid}/cancel-request`));
                EventBus.$emit('cancel-payment-intent-success', responseData.payment);
                commit(types.SET_PAYMENT, responseData.payment);
            } catch (error) {
                handleApiError(error);
            }
        },

        /**
         * Refund a payment
         */
        refundPayment({state, commit}, payment: RefundData) {
            commit('SET_PAYMENTS_LOADING', {subject: 'refundPayment', loading: true});
            state.paymentForm.errors.clear();

            axios.post(`/v1/payments/${payment.uuid}/refund`, payment)
                .then((response) => {
                    EventBus.$emit('refund-payment-success');
                    commit('SET_PAYMENT', response.data.payment);
                    push.success({
                        message: 'Refund Payment Success',
                    });
                }).catch((e) => {
                EventBus.$emit('refund-payment-failure', e?.response?.data?.message || 'There was an error');
                state.paymentForm.errors.set(e?.response?.data?.message || 'There was an error');
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'refundPayment', loading: false});
            });
        },

        /**
         * Keep deposit
         */
        keepDeposit({state, commit}, payment) {
            commit('SET_PAYMENTS_LOADING', {subject: 'refundPayment', loading: true});
            state.paymentForm.errors.clear();

            axios.post(`/v1/company/${StorageService.getCompanyUuid()}/payments/hold-deposit`, payment)
                .then((response) => {
                    EventBus.$emit('refund-payment-success');
                    commit('SET_PAYMENT', response.data.payment);
                    push.success({
                        message: 'Refund Payment Success',
                    });
                }).catch((e) => {
                state.paymentForm.errors.set(e?.response?.data?.message || 'There was an error');
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'refundPayment', loading: false});
            });
        },

        deletePayment({commit}, uuid: string) {
            commit('SET_PAYMENTS_LOADING', {subject: 'deletePayment', loading: true});

            axios.delete(`/v1/payments/${uuid}`)
                .then(() => {
                    EventBus.$emit('delete-payment-success');
                }).catch((e) => {
                push.error({
                    message: e?.response?.data?.message || 'There was an error',
                });
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'deletePayment', loading: false});
            });
        },

        async deleteCustomPaymentMethod({commit}, name) {
            const responseData = await performApiRequest(commit, 'SET_PAYMENTS_LOADING', 'deleteCustomPayment', () => axios.delete(`/v1/custom-payment-methods/${name}`));
            commit(types.SET_CUSTOM_PAYMENT_METHODS, responseData.meta);
        },


        /**
         * Send a sms with a payment request link
         */
        sendPaymentRequestLinkViaSms({commit}, payment) {
            commit('SET_PAYMENTS_LOADING', {subject: 'sendPaymentViaSms', loading: true});

            axios.post(`/v1/payments/${payment.uuid}/send-via-sms`)
                .then(() => {
                    EventBus.$emit('send-payment-request-link-via-sms-success');
                }).catch((e) => {
                EventBus.$emit('send-payment-request-link-via-sms-failed', e?.response?.data?.message || 'There was an error');
            }).finally(() => {
                commit('SET_PAYMENTS_LOADING', {subject: 'sendPaymentViaSms', loading: false});
            });
        },
    },
    getters: {
        loading: (state: PaymentsState) => state.loading,
        payment: (state: PaymentsState) => state.payment,
        payments: (state: PaymentsState) => state.payments,
        newPayment: (state: PaymentsState) => state.newPayment,
        stripeKey: (state: PaymentsState) => state.stripeKey,
        StripeIntent: (state: PaymentsState) => state.StripeIntent,
        paymentMethods: (state: PaymentsState) => state.payment_methods,
        paymentForm: (state: PaymentsState) => state.paymentForm,
        customPaymentMethods: (state: PaymentsState) => state.customPaymentMethods,
        refundDialog: (state: PaymentsState) => state.refundDialog,
    }
};

export default PaymentsModule;
